<template>
  <v-app>
    <v-app-bar app color="dark" dark>
      <v-toolbar-title>
        Layout Labelling
        <span class="text-caption ml-3" style="opacity:0.75">{{ stageLabel }}</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <span v-if="items.length" class="mr-4 font-weight-medium">
        {{ currentIndex + 1 }} / {{ items.length }}
      </span>
    </v-app-bar>

    <v-main>
      <v-container :fill-height="intro">
        <v-row align="center" justify="center" v-if="intro && mode === 'practice'">
          <v-col cols="12">
            <v-card class="intro-card text-center">
              <h2 class="intro-title">Practice Trials</h2>
              <p class="intro-body">
                You will now do a practice trial of 4 questions. Find the shortest path between
                the two red/circled nodes (each marked with a dark ring), count its edges, and
                pick your answer. You get feedback after each trial.
                <span style="font-weight: bold; color: #1F3864;">
                  You are required to get 3 of 4 correct to continue.
                </span>
                If you do not, you will see the training again with a new set of practice trials.
              </p>
              <v-btn color="primary" class="intro-actions" @click="intro = false">Start practice</v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-row align="center" justify="center" v-if="intro && mode === 'screening'">
          <v-col cols="12">
            <v-card class="intro-card text-center">
              <h2 class="intro-title">Final Check Before the Main Study</h2>
              <p class="intro-body">
                Now you will be asked 5 questions like the ones you practiced. No feedback is
                shown.
                <span style="font-weight: bold; color: #1F3864;">
                  You need at least 4 of 5 correct to pass and proceed to the main study.
                </span>
                Once you pick both an answer and an effort rating, the next question loads
                automatically.
              </p>
              <v-btn color="primary" class="intro-actions" @click="intro = false">Start</v-btn>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="!intro && currentItem">
          <div class="text-center mb-1 text-subtitle-1">
            {{ mode === 'practice' ? 'Trial' : 'Question' }}
            {{ currentIndex + 1 }} of {{ items.length }}
          </div>

          <v-row class="mb-2">
            <v-col cols="12" class="text-center py-1">
              <v-card class="mx-auto study-banner">
                <v-img :src="currentItem.stimulus" contain height="100%" width="100%" class="grey lighten-3">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card class="mx-auto pa-3 study-answers">
                <p class="path-prompt text-center mb-1">
                  What is the length of the shortest path between the two red/circled nodes?
                </p>
                <p class="path-prompt text-center mb-1">Shortest path length:</p>
                <div class="button-choice-container answer-block">
                  <v-btn-toggle
                    v-model="answer"
                    class="button-choice-group"
                    :disabled="locked"
                    @change="trySubmit"
                  >
                    <v-btn
                      v-for="n in 10"
                      :key="'a'+n"
                      :value="n"
                      color="primary"
                      outlined
                      class="choice-button"
                    >{{ n }}</v-btn>
                  </v-btn-toggle>
                </div>

                <p class="effort-prompt font-weight-medium text-center mb-1">
                  Rate the effort you devoted on a scale from 1 (lowest) to 9 (highest):
                </p>
                <div class="button-choice-container">
                  <v-btn-toggle
                    v-model="effort"
                    class="button-choice-group"
                    :disabled="locked"
                    @change="trySubmit"
                  >
                    <v-btn
                      v-for="n in 9"
                      :key="'e'+n"
                      :value="n"
                      color="primary"
                      outlined
                      class="choice-button"
                    >{{ n }}</v-btn>
                  </v-btn-toggle>
                </div>

                <v-alert
                  v-if="feedback"
                  :type="feedbackOk ? 'success' : 'error'"
                  dense
                  class="mt-4"
                >
                  {{ feedback }}
                </v-alert>

                <div class="text-center mt-4" v-if="mode === 'practice' && feedback">
                  <v-btn color="primary" @click="nextPractice">Next</v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import config from '@/config';

export default {
  name: 'PilotTrials',
  props: {
    mode: { type: String, required: true }, // 'practice' | 'screening'
  },
  data() {
    return {
      sessionId: this.$route.params.sessionId,
      items: [],
      attempt: 1,
      currentIndex: 0,
      answer: null,
      effort: null,
      locked: false,
      feedback: null,
      feedbackOk: false,
      intro: true,
      t0: Date.now(),
      loading: false,
    };
  },
  computed: {
    stageLabel() {
      return this.mode === 'practice' ? 'Practice' : 'Screening';
    },
    currentItem() {
      return this.items[this.currentIndex] || null;
    },
  },
  async created() {
    await this.loadItems();
  },
  methods: {
    async loadItems() {
      const base = `${config.API_BASE_URL}/prolific/session/${this.sessionId}`;
      const url = this.mode === 'practice'
        ? `${base}/practice/questions/`
        : `${base}/screening/questions/`;
      const resp = await fetch(url);
      const data = await resp.json();
      this.items = data.items || [];
      this.attempt = data.attempt || 1;
      this.resetTrial();
    },
    resetTrial() {
      this.answer = null;
      this.effort = null;
      this.locked = false;
      this.feedback = null;
      this.t0 = Date.now();
    },
    async trySubmit() {
      if (!this.answer || !this.effort || this.locked || this.loading) return;
      this.locked = true;
      this.loading = true;
      const base = `${config.API_BASE_URL}/prolific/session/${this.sessionId}`;
      const endpoint = this.mode === 'practice' ? 'practice/submit/' : 'screening/submit/';
      try {
        const resp = await fetch(`${base}/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attempt: this.attempt,
            trial: this.currentIndex + 1,
            answer: this.answer,
            effort: this.effort,
            rt_ms: Date.now() - this.t0,
          }),
        });
        const data = await resp.json();
        if (this.mode === 'practice') {
          this.feedbackOk = !!data.is_correct;
          this.feedback = data.is_correct
            ? `Great job! The answer is ${data.correct_answer}.`
            : `Not quite. The correct answer is ${data.correct_answer}.`;
        } else {
          // Screening: auto-advance with no feedback
          setTimeout(() => this.advanceScreening(), 250);
        }
      } catch (e) {
        console.error(e);
        this.locked = false;
      } finally {
        this.loading = false;
      }
    },
    async nextPractice() {
      if (this.currentIndex + 1 < this.items.length) {
        this.currentIndex += 1;
        this.resetTrial();
        return;
      }
      await this.finishPractice();
    },
    async advanceScreening() {
      if (this.currentIndex + 1 < this.items.length) {
        this.currentIndex += 1;
        this.resetTrial();
        return;
      }
      await this.finishScreening();
    },
    async finishPractice() {
      const resp = await fetch(
        `${config.API_BASE_URL}/prolific/session/${this.sessionId}/practice/finish/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attempt: this.attempt }),
        }
      );
      const data = await resp.json();
      if (data.passed) {
        this.$router.replace(`/prolific/study/${this.sessionId}/screening`);
      } else if (data.retry) {
        alert('You will see the training again, followed by a new set of practice trials.');
        this.$router.replace(`/prolific/study/${this.sessionId}/video`);
      } else {
        this.$router.replace(`/prolific/study/${this.sessionId}/training-result`);
      }
    },
    async finishScreening() {
      const resp = await fetch(
        `${config.API_BASE_URL}/prolific/session/${this.sessionId}/screening/finish/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      const data = await resp.json();
      if (data.passed) {
        this.$router.replace(`/prolific/study/${this.sessionId}/main-intro`);
      } else {
        this.$router.replace(`/prolific/study/${this.sessionId}/training-result`);
      }
    },
  },
};
</script>

<style scoped>
.intro-card {
  max-width: 1150px;
  margin-left: auto;
  margin-right: auto;
  padding: 44px !important;
}
.intro-title {
  color: #111827;
  font-weight: 700;
  font-size: 1.75rem;
  margin-bottom: 20px;
  line-height: 1.25;
}
.intro-body {
  font-size: 1.05rem;
  line-height: 1.55;
}
.intro-actions {
  margin-top: 24px;
}
.study-banner {
  height: 49vh !important;
  width: 64.9vh !important;
  min-width: unset !important;
  max-width: 100% !important;
  margin: 0 auto !important;
}
.study-answers {
  width: 100%;
  max-width: 760px;
}
.answer-block {
  margin-bottom: 14px;
}
.path-prompt {
  font-size: 21px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
}
.effort-prompt {
  font-size: 21px;
  line-height: 1.25;
  white-space: nowrap;
}
.button-choice-container {
  text-align: center;
}
.button-choice-group {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 6px;
}
.choice-button {
  min-width: 48px !important;
  height: 48px !important;
  padding: 0 8px !important;
  font-size: 16px;
  font-weight: bold;
}
.choice-button.v-btn--active {
  background-color: #1976d2 !important;
  color: white !important;
}
</style>
